/**
 * CONVERSAS — o que está sendo dito, em todos os canais, num lugar só.
 *
 * A conversa é POR CONTATO, não por número. O lead recebe a campanha pelo
 * Disparador 03, responde, é redirecionado ao SDR em outro chip e continua o
 * assunto lá: dois números, uma conversa. Esta tela junta.
 *
 * DUAS DECISÕES DE LEITURA QUE NÃO SÃO ÓBVIAS
 *
 * 1. A ordem é por QUEM ESPERA HÁ MAIS TEMPO, não pela mensagem mais recente.
 *    Ordenar por recência colocaria no topo justamente a conversa que acabou
 *    de ser respondida — o contrário do que quem atende precisa ver.
 *
 * 2. Cada mensagem mostra a ORIGEM: humano, IA ou sistema. Não é rótulo
 *    decorativo. Foi a ausência desse discriminador que produziu o bug dos
 *    74%: o sistema gravava o próprio rastro no histórico e cegava a trava
 *    que usava para decidir enviar. Aqui ele fica à vista.
 */
import { useCallback, useState } from "react";
import { api } from "../api.js";
import { Aviso, Botao, Indicador, Painel, Selo, classeEntrada } from "../ui/base.js";
import { Conteudo, Indicadores, useCarga } from "../ui/comercial.js";

interface Conversa {
  contatoId: string;
  nome: string | null;
  telefone: string;
  mensagens: number;
  recebidas: number;
  enviadas: number;
  ultimaEm: string;
  ultimaRecebidaEm: string | null;
  numerosEnvolvidos: number;
  aguardandoResposta: boolean;
  ultimoTexto: string | null;
  ultimaDirecao: string | null;
}

/** Rótulo e tom por origem. `sistema` inclui o que o motor escreve sozinho. */
const ORIGEM: Record<string, { rotulo: string; tom: "ok" | "cool" | "idle" }> = {
  humano: { rotulo: "humano", tom: "ok" },
  ia: { rotulo: "IA", tom: "cool" },
  sistema: { rotulo: "sistema", tom: "idle" },
};

function quandoRelativo(iso: string): string {
  const min = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (min < 1) return "agora";
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `há ${h}h`;
  const d = Math.floor(h / 24);
  return d === 1 ? "ontem" : `há ${d} dias`;
}

function hora(iso: string): string {
  return new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function Conversas(): JSX.Element {
  const [busca, setBusca] = useState("");
  const [soAguardando, setSoAguardando] = useState(false);
  const [aberta, setAberta] = useState<string | null>(null);

  const { estado, recarregar } = useCarga(
    useCallback(
      () =>
        api.conversas({
          ...(busca.trim() ? { busca: busca.trim() } : {}),
          ...(soAguardando ? { apenasAguardando: "1" } : {}),
        }),
      [busca, soAguardando],
    ),
  );

  if (aberta) {
    return <Thread contatoId={aberta} aoFechar={() => setAberta(null)} />;
  }

  return (
    <div className="grid gap-4">
      <Conteudo estado={estado} aoRecarregar={recarregar}>
        {(d) => {
          const itens = d.itens as Conversa[];
          const esperando = itens.filter((c) => c.aguardandoResposta);
          const espalhadas = itens.filter((c) => c.numerosEnvolvidos > 1);

          return (
            <div className="grid gap-4">
              <Indicadores>
                <Indicador
                  rotulo="Conversas"
                  valor={itens.length}
                  nota="Uma por contato, somando todos os números"
                />
                <Indicador
                  rotulo="Esperando resposta"
                  valor={esperando.length}
                  nota="O contato falou por último"
                />
                <Indicador
                  rotulo="Em mais de um número"
                  valor={espalhadas.length}
                  nota="Passaram do disparo para o atendimento"
                />
              </Indicadores>

              <Painel
                titulo="Fila de atendimento"
                dica="Ordenada por quem espera há mais tempo — não pela mensagem mais recente"
                acoes={
                  <div className="flex gap-2 items-center">
                    <input
                      className={`${classeEntrada} py-1 text-[12.5px] w-[180px]`}
                      placeholder="nome ou telefone"
                      value={busca}
                      onChange={(e) => setBusca(e.target.value)}
                    />
                    <Botao
                      pequeno
                      tipo={soAguardando ? "principal" : "fantasma"}
                      onClick={() => setSoAguardando((v) => !v)}
                    >
                      Só quem espera
                    </Botao>
                  </div>
                }
              >
                {itens.length === 0 ? (
                  <Aviso tom="cool">
                    Nenhuma conversa ainda. Elas aparecem sozinhas: toda mensagem
                    que entra ou sai pelo webhook da Evolution vira evento, e o
                    evento traz o contato junto. Enquanto nenhuma campanha tiver
                    saído e nenhum lead tiver respondido, esta tela fica vazia — e
                    isso é o esperado, não uma falha.
                  </Aviso>
                ) : (
                  <ul className="grid gap-1.5">
                    {itens.map((c) => (
                      <li key={c.contatoId}>
                        <button
                          onClick={() => setAberta(c.contatoId)}
                          className="w-full text-left rounded-[10px] border border-linha px-4 py-3 hover:bg-superficie-3 transition-colors"
                        >
                          <div className="flex items-baseline gap-2 flex-wrap">
                            <strong className="text-[13.5px]">
                              {c.nome ?? c.telefone}
                            </strong>
                            {c.nome && (
                              <span className="text-[12px] text-tinta-3">
                                {c.telefone}
                              </span>
                            )}
                            {c.aguardandoResposta && (
                              <Selo tom="ember">esperando resposta</Selo>
                            )}
                            {c.numerosEnvolvidos > 1 && (
                              <Selo tom="cool">
                                {c.numerosEnvolvidos} números
                              </Selo>
                            )}
                            <span className="ml-auto text-[11.5px] text-tinta-3 whitespace-nowrap">
                              {quandoRelativo(c.ultimaEm)}
                            </span>
                          </div>
                          {c.ultimoTexto && (
                            <div className="mt-1 text-[12.5px] text-tinta-2 line-clamp-2">
                              <span className="text-tinta-3">
                                {c.ultimaDirecao === "recebida" ? "› " : "‹ "}
                              </span>
                              {c.ultimoTexto}
                            </div>
                          )}
                          <div className="mt-1 text-[11.5px] text-tinta-3">
                            {c.mensagens} mensagem(ns) — {c.recebidas} recebida(s),{" "}
                            {c.enviadas} enviada(s)
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </Painel>
            </div>
          );
        }}
      </Conteudo>
    </div>
  );
}

function Thread({
  contatoId,
  aoFechar,
}: {
  contatoId: string;
  aoFechar: () => void;
}): JSX.Element {
  const { estado, recarregar } = useCarga(
    useCallback(() => api.conversa(contatoId), [contatoId]),
  );

  return (
    <Conteudo estado={estado} aoRecarregar={recarregar}>
      {(d) => {
        // Quantos chips diferentes aparecem. É a informação que justifica esta
        // tela existir — e a que some quando se olha conversa por número.
        const numeros = [
          ...new Set(d.mensagens.map((m) => m.numeroExibicao).filter(Boolean)),
        ];

        return (
          <div className="grid gap-4">
            <Painel
              titulo={d.contato.nome ?? d.contato.telefone}
              dica={
                numeros.length > 1
                  ? `${d.contato.telefone} · a conversa passou por ${numeros.length} números`
                  : d.contato.telefone
              }
              acoes={
                <Botao pequeno tipo="fantasma" onClick={aoFechar}>
                  Voltar
                </Botao>
              }
            >
              {d.mensagens.length === 0 ? (
                <Aviso tom="cool">Nenhuma mensagem registrada para este contato.</Aviso>
              ) : (
                <div className="grid gap-2.5">
                  {d.mensagens.map((m, i) => {
                    const anterior = d.mensagens[i - 1];
                    const trocouNumero =
                      anterior && anterior.numeroExibicao !== m.numeroExibicao;
                    const recebida = m.direcao === "recebida";
                    const o = ORIGEM[m.origem] ?? { rotulo: m.origem, tom: "idle" as const };

                    return (
                      <div key={m.id}>
                        {trocouNumero && (
                          // A troca de chip é marcada no meio da thread: é o
                          // momento em que o disparo virou atendimento, e sem
                          // isso a conversa parece uma só linha reta.
                          <div className="flex items-center gap-2 my-2">
                            <div className="h-px flex-1 bg-linha" />
                            <span className="text-[11px] text-tinta-3">
                              passou para {m.numeroExibicao ?? "outro número"}
                            </span>
                            <div className="h-px flex-1 bg-linha" />
                          </div>
                        )}
                        <div
                          className={`max-w-[76%] rounded-[10px] px-3.5 py-2.5 ${
                            recebida
                              ? "bg-superficie-3"
                              : "bg-marca-suave ml-auto"
                          }`}
                        >
                          <div className="text-[13px] whitespace-pre-line leading-relaxed">
                            {m.texto ?? (
                              <span className="text-tinta-3 italic">
                                {m.tipo === "texto"
                                  ? "sem texto registrado"
                                  : `[${m.tipo}]`}
                              </span>
                            )}
                          </div>
                          <div className="mt-1.5 flex items-center gap-2 flex-wrap text-[10.5px] text-tinta-3">
                            <span>{hora(m.ocorridoEm)}</span>
                            <Selo tom={o.tom}>{o.rotulo}</Selo>
                            {m.numeroExibicao && <span>{m.numeroExibicao}</span>}
                            {m.campanhaNome && <span>· {m.campanhaNome}</span>}
                            {m.status && <span>· {m.status}</span>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Painel>

            <Painel titulo="Por que a origem aparece em toda mensagem">
              <p className="text-[13px] text-tinta-2 leading-relaxed">
                <strong>humano</strong>, <strong>IA</strong> e{" "}
                <strong>sistema</strong> ficam à vista porque já custaram caro
                misturados. No incidente dos 74%, o follow-up gravava o próprio
                rastro no histórico como se fosse mensagem de gente — e com isso
                cegava a trava de 24h que ele mesmo consultava para decidir
                enviar. Toda leitura que alimenta decisão precisa filtrar por
                aqui.
              </p>
            </Painel>
          </div>
        );
      }}
    </Conteudo>
  );
}
